import React from 'react'
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ViewListIcon from '@mui/icons-material/ViewList'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import SortIcon from '@mui/icons-material/Sort'

export type ViewMode = 'grid' | 'list'
export type SortOption = 'createdAt,desc' | 'createdAt,asc' | 'title,asc' | 'title,desc' | 'fileSize,desc' | 'fileSize,asc'

type Props = {
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  sortOption: SortOption
  onSortChange: (sort: SortOption) => void
  /** Callback para abrir busca avançada */
  onAdvancedSearch?: () => void
}

export default function Toolbar({
  viewMode,
  onViewModeChange,
  sortOption,
  onSortChange,
  onAdvancedSearch
}: Props) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        py: { xs: 1.5, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        bgcolor: '#fff',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      {/* Linha 1: Busca */}
      {onAdvancedSearch && (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<SearchIcon />}
          onClick={onAdvancedSearch}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            py: { xs: 1.25, sm: 1.5 },
            px: 2,
            borderColor: 'divider',
            color: 'text.secondary',
            bgcolor: alpha('#000', 0.015),
            justifyContent: 'flex-start',
            fontSize: { xs: '0.9rem', sm: '0.95rem' },
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: alpha('#1976d2', 0.04)
            },
            // O tema WP aplica background escuro em focus/active nos <button>.
            // Usando #monkee-file-transcript-client & geramos especificidade (1,2,0)
            // que supera o tema WP e o isolation CSS.
            '#monkee-file-transcript-client &:focus': {
              backgroundColor: `${alpha('#000', 0.015)} !important`,
              outline: 'none !important',
            },
            '#monkee-file-transcript-client &:focus-visible': {
              backgroundColor: `${alpha('#000', 0.015)} !important`,
              outline: 'none !important',
            },
            '#monkee-file-transcript-client &:active': {
              backgroundColor: `${alpha('#1976d2', 0.08)} !important`,
            },
            '#monkee-file-transcript-client &:hover': {
              borderColor: 'primary.main',
              backgroundColor: `${alpha('#1976d2', 0.04)} !important`,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              color: 'text.disabled',
              fontWeight: 400
            }}
          >
            Buscar em transcrições...
          </Box>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              ml: 'auto',
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            <Chip
              label="Ctrl+K"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: alpha('#000', 0.06)
              }}
            />
            <Chip
              label="Ctrl+A seleciona todos"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: alpha('#1976d2', 0.1),
                color: 'primary.main'
              }}
            />
          </Stack>
        </Button>
      )}

      {/* Linha 2: Controles */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ flex: 1 }} />

        {/* Ordenação */}
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: 120, sm: 150 },
            // ─── Overrides do isolation CSS do WordPress ───────────────────────
            // O isolation CSS em main.tsx injeta regras com (1,1,0)+!important.
            // Usando o prefixo '#monkee-file-transcript-client &' geramos (1,2,0)+!important,
            // que vence tanto o isolation CSS quanto o tema WordPress.

            // 1) .MuiOutlinedInput-root: isolation força border-radius 4px
            '#monkee-file-transcript-client & .MuiOutlinedInput-root': {
              borderRadius: '6px !important',
              backgroundColor: 'transparent !important',
            },
            // 2) .MuiOutlinedInput-notchedOutline: isolation força border-radius 4px + border escuro
            '#monkee-file-transcript-client & .MuiOutlinedInput-notchedOutline': {
              borderRadius: '6px !important',
              borderColor: 'rgba(0,0,0,0.15) !important',
            },
            // 3) .MuiInputBase-input: isolation força padding 16.5px 14px (TextField padrão)
            //    que quebra o tamanho compacto do Select (só o right-padding precisa de espaço pro ícone)
            '#monkee-file-transcript-client & .MuiInputBase-input.MuiSelect-select': {
              padding: '4.8px 32px 4.8px 4px !important',
              backgroundColor: 'transparent !important',
            },
          }}
        >
          <Select
            value={sortOption}
            onChange={e => onSortChange(e.target.value as SortOption)}
            displayEmpty
            startAdornment={<SortIcon sx={{ mr: 0.5, fontSize: 16, color: 'action.active' }} />}
            sx={{
              borderRadius: 1.5,
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              '& .MuiSelect-select': { py: 0.6, pl: 0.5 }
            }}
          >
            <MenuItem value="createdAt,desc">Mais recentes</MenuItem>
            <MenuItem value="createdAt,asc">Mais antigos</MenuItem>
            <MenuItem value="title,asc">Nome A-Z</MenuItem>
            <MenuItem value="title,desc">Nome Z-A</MenuItem>
            <MenuItem value="fileSize,desc">Maior tamanho</MenuItem>
            <MenuItem value="fileSize,asc">Menor tamanho</MenuItem>
          </Select>
        </FormControl>

        {/* Toggle de visualização */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, mode) => mode && onViewModeChange(mode)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              border: '1px solid',
              borderColor: 'divider',
              px: 0.75,
              py: 0.5,
              // Impede que o tema WP aplique background branco nos botões de toggle
              '#monkee-file-transcript-client &': {
                backgroundColor: 'transparent !important',
              },
              '&.Mui-selected': {
                bgcolor: alpha('#1976d2', 0.08),
                color: 'primary.main',
                borderColor: 'primary.main',
                '#monkee-file-transcript-client &': {
                  backgroundColor: `${alpha('#1976d2', 0.08)} !important`,
                },
              }
            },
            // ─── Travas de altura para WordPress ────────────────────────────────────
            // O tema WP pode inflar o padding de <button> ou o isolation CSS
            // força .MuiSvgIcon-root para font-size: 1.5rem (24px) via (1,1,0)+!important.
            // Usando '#monkee-file-transcript-client &' geramos (1,2,0)+!important,
            // travando padding e tamanho de ícone para que a altura seja idêntica ao browser.
            '#monkee-file-transcript-client & .MuiToggleButton-root': {
              padding: '4px 6px !important',   // py:0.5 = 4px, px:0.75 = 6px
              lineHeight: '1 !important',
              minWidth: '32px !important',
              height: '32px !important',
            },
            '#monkee-file-transcript-client & .MuiSvgIcon-root': {
              fontSize: '18px !important',     // anula o 1.5rem (24px) do isolation CSS
              width: '18px !important',
              height: '18px !important',
            },
          }}
        >
          <ToggleButton value="list" aria-label="Visualizar em lista">
            <ViewListIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
          <ToggleButton value="grid" aria-label="Visualizar em grade">
            <ViewModuleIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  )
}
